import React, {useState, useEffect, useContext} from "react";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import { Box, Button, Container } from "@amzn/awsui-components-react";
import ErrorAlert from "../common/ErrorAlert";
import { ExecutionsTable } from "./table/executionsTable";
import { OrdersTable } from "./table/ordersTable";
import { useRedriveData } from "./hooks/useRedriveData";
import { useRedriveOrdersData } from "./hooks/useRedriveOrdersData";
import { useFileValidation } from "./hooks/useFileValidation";
import { StageSelector } from "./components/StageSelector";
import { FileUploader } from "./components/FileUploader";
import { useExecuteRedrive } from "./hooks/useExecuteRedrive";
import {StageContext} from "../../contexes/OrderIdContext";
import { Stage } from "../../lusca-search-api/generated-src";

interface Props {
  setStage: React.Dispatch<React.SetStateAction<Stage>>;
}

export default function OrderRedriveContent({ setStage }: Props) {
  const stage = useContext(StageContext);
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Parse URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const executionId = urlParams.get('executionId');
    if (executionId) {
      setSelectedExecutionId(executionId);
    }
  }, []);

  // Custom hooks
  const { pastRedrives, loading: loadingRedrives, error: fetchError } = useRedriveData(stage);
  const { orders, loading: loadingOrders, error: ordersError, isTruncated, downloadUrl } = useRedriveOrdersData(stage, selectedExecutionId);
  const {
    file,
    orderCount,
    error: fileError,
    redriveOK,
    handleFileChange,
    validateRedrive,
    setError,
    removeDuplicates
  } = useFileValidation();
  const { executeRedrive: executeRedriveHook } = useExecuteRedrive();

  // Combine errors from all sources
  const error = [fetchError, fileError, ordersError].filter(Boolean).join('; ') || null;

  const executeRedrive = async () => {
    if (file) {
      setLoading(true);
      await executeRedriveHook(file, stage, setSelectedExecutionId, setError);
      setLoading(false);
    }
  };

  // Handle back button click
  const handleBackToRedrives = () => {
    setSelectedExecutionId(null);
    // Remove executionId parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete('executionId');
    window.history.pushState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
  };

  return (
    <SpaceBetween size="xl">
      <div>
        <SpaceBetween size="l">
          {error && <ErrorAlert content={error} header="Redrive Error" />}
          {selectedExecutionId && (
            <Button
              iconName="arrow-left"
              onClick={handleBackToRedrives}
              variant="link"
            >
              Back to all redrives
            </Button>
          )}

          <Container header={<Box variant="h2">Order Redrive Tool</Box>}>
            <SpaceBetween size="m">
              <Box variant="p">
                This tool allows you to redrive orders that have already been
                processed. If an order is marked as healthy, but an error was
                present in an upstream system like FLARE, you will be able to
                reprocess the CBE Generated by lusca with this tool. You can
                upload a CSV file with Order IDs here, or redrive a single
                order on that order&#39;s page.
              </Box>
              {!selectedExecutionId && (
                <StageSelector
                  stage={stage}
                  setStage={setStage}
                />
              )}
            </SpaceBetween>
          </Container>

          {!selectedExecutionId && (
            <Container>
              <SpaceBetween size="m">
                <FileUploader
                  file={file}
                  handleFileChange={handleFileChange}
                  validateRedrive={validateRedrive}
                  redriveOK={redriveOK}
                  removeDuplicates={removeDuplicates}
                  loading={loading}
                />
                {file && redriveOK && (
                  <Button
                    onClick={executeRedrive}
                    variant="primary"
                    disabled={loading}
                    loading={loading}
                  >
                    Execute Redrive
                  </Button>
                )}
              </SpaceBetween>
            </Container>
          )}

          {selectedExecutionId ? (
            <OrdersTable
              items={orders}
              loading={loadingOrders}
              executionId={selectedExecutionId}
              stage={stage}
              isTruncated={isTruncated}
              downloadUrl={downloadUrl}
            />
          ) : (
            <ExecutionsTable
              items={pastRedrives}
              loading={loadingRedrives}
            />
          )}
        </SpaceBetween>
      </div>
    </SpaceBetween>
  );
}
